/**
 * Shared Clinic-OS primitives for the legacy admin screens (QnAs, Support Tasks,
 * User Data, Appointments, Subscription, Payment Link, Night Duty, Book
 * Consultation, Doctors Appointments). The worklist's ui.ts stays the single
 * source of truth for buttons/focus/micro-labels — this module re-exports it and
 * adds the screen-level primitives those pages need (page header, tables, inputs,
 * empty states) so nine screens can't each invent their own.
 *
 * COLOUR RULES (see tailwind.config.js + Worklist/ui.ts):
 *   clinic-* = neutrals/ink · crit-* / warn-* = severity ONLY · action-* = the ONE
 *   interactive teal. `slate-*` does not exist in this theme (silently no-ops) and
 *   `red-50` is a 10%-alpha wash — use crit-50. Tailwind 3.1: no line-clamp/
 *   text-wrap utilities; `divide-*` beats a child's border-side colour, use
 *   `![border-left-color:#...]` when a row rail sits inside a divided list.
 */

export {
  btnBase,
  dangerBtn,
  focusRing,
  ghostBtn,
  microLabel,
  primaryBtn,
} from "components/Worklist/ui";
import { focusRing } from "components/Worklist/ui";

/* ---------- page header ---------- */

/** Tracked kicker above every page title — same voice as the Worklist. */
export const pageKicker =
  "font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-clinic-400 dark:text-gray-500";

export const pageTitle =
  "mt-0.5 text-[22px] font-bold leading-tight tracking-tight text-clinic-900 dark:text-white";

export const pageSubtitle = "mt-0.5 text-sm text-clinic-500 dark:text-gray-400";

/* ---------- tables (instrument, not spreadsheet) ---------- */

/** Wrapper panel: one hairline border, rows divided inside. */
export const tablePanel =
  "overflow-x-auto rounded-lg border border-clinic-200 bg-white shadow-sm dark:border-navy-700 dark:!bg-navy-800";

export const table = "w-full border-collapse text-left";

/** Column header: mono uppercase micro-label on a neutral ground — never a colour bar. */
export const th =
  "whitespace-nowrap border-b border-clinic-200 bg-clinic-50 px-3 py-2 text-left font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-clinic-500 dark:border-navy-700 dark:!bg-navy-900 dark:text-gray-400";

export const td =
  "border-b border-clinic-100 px-3 py-2.5 align-top text-[13px] leading-snug text-clinic-700 dark:border-navy-700 dark:text-gray-200";

export const trHover =
  "transition-colors hover:bg-clinic-50 motion-reduce:transition-none dark:hover:bg-navy-700";

/* ---------- form controls ---------- */

export const input = `w-full rounded-md border border-clinic-300 bg-white px-3 py-1.5 text-sm text-clinic-900 placeholder:text-clinic-400 dark:border-navy-600 dark:!bg-navy-900 dark:text-white ${focusRing}`;

export const select = `cursor-pointer rounded-md border border-clinic-300 bg-white px-2.5 py-1.5 text-sm text-clinic-700 hover:bg-clinic-50 dark:border-navy-600 dark:!bg-navy-900 dark:text-gray-200 ${focusRing}`;

/** Field label: the same micro-label voice, sitting above its control. */
export const fieldLabel =
  "mb-1 block font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-clinic-500 dark:text-gray-400";

/* ---------- in-place states ---------- */

/** Empty state lives IN the surface it describes — never a toast in a corner. */
export const emptyState =
  "px-5 py-10 text-center text-sm text-clinic-500 dark:text-gray-400";

/** Count badge — neutral by default; severity only when the number IS an alarm. */
export const countBadge =
  "rounded bg-clinic-100 px-1.5 py-px font-mono text-[11px] font-semibold tabular-nums text-clinic-600 dark:bg-navy-700 dark:text-gray-300";

/** Text-only action inside dense cells (e.g. "Open chat") — teal, underlined on hover. */
export const inlineLink = `cursor-pointer rounded font-medium text-action-600 hover:text-action-700 hover:underline dark:text-action-300 ${focusRing}`;
